import { Component, OnInit } from '@angular/core';
import { CancellationStat, SubscriptionType, Colors, ColorsTrend } from "../../exit-survey.interfaces";
import { Chart } from 'chart.js';
import { ExitSurveyService } from '../../services/exit-survey.service';
const moment = require("moment");
const alertify = require("alertify.js");

@Component({
  selector: 'ch-cancellations-statistics',
  templateUrl: './cancellations-statistics.component.html',
  styleUrls: ['./cancellations-statistics.component.css'],
})
export class CancellationsStatisticsComponent implements OnInit {
  public cancellationStats: CancellationStat[] = [];
  public historicalCancellationsChart = null as Chart;
  public upcomingCancellationsChart = null as Chart;
  private isLoading = false;

  public cancellationPeriods = [
    { value: '1', name: 'Last month' },
    { value: '3', name: 'Last 3 months' },
    { value: '12', name: 'Year to date' },
  ];
  public cancellationPeriod = '12';

  public subscriptionTypes = [
    { value: SubscriptionType.ALL, name: 'All subscriptions' },
    { value: SubscriptionType.MONTH, name: 'Monthly subscriptions' },
    { value: SubscriptionType.YEAR, name: 'Annual subscriptions' },
  ];
  public subscriptionType = SubscriptionType.ALL;

  constructor(
    private exitSurveyService: ExitSurveyService
  ) { }

  ngOnInit(): void {
    this.loadCancellations();
  }

  private calculateTrendLine(yValues: number[]) {
    if (!Array.isArray(yValues) || yValues.length < 2) {
      return [];
    }

    const n = yValues.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += yValues[i];
      sumXY += i * yValues[i];
      sumX2 += i * i;
    }

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    const trendLineYValues = [];
    for (let i = 0; i < n; i++) {
      const x = i;
      const y = slope * x + intercept;
      trendLineYValues.push(y);
    }

    return trendLineYValues;
  }

  private loadCancellations() {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.exitSurveyService.getCancellationStats({
      since: moment().subtract(+this.cancellationPeriod, 'months').format('YYYY-MM-DD'),
      type: this.subscriptionType,
    })
      .subscribe(
      result => {
        const data = result.json()
        this.cancellationStats = data;
        this.updateHistoricalCancellationsChartData();
        this.updateUpcomingCancellationsChartData();
        this.isLoading = false;
      },
      error => {
        alertify.error(error);
        this.isLoading = false;
      });
  }

  public cancellationPeriodChanged(event) {
    const value = event.target.value;
    this.cancellationPeriod = value;
    this.loadCancellations();
  }

  public subscriptionTypeChanged(event) {
    const value = event.target.value;
    this.subscriptionType = value;
    this.loadCancellations();
  }

  get historicalCancellationCountsByMonth() {
    const historicalStats = this.cancellationStats.filter(item =>
      item.cancellationReason === 'CANCELLED' || item.cancellationReason === 'LAPSED'
    );

    let months = historicalStats.map(item => item.date)
    months = months.filter((item, index) => months.indexOf(item) === index)
      .map(item => moment(item))
      .sort((a, b) => a.unix() - b.unix());

    let reasons = historicalStats.map(item => item.cancellationReason)
    reasons = reasons.filter((item, index) => reasons.indexOf(item) === index);

    const aggregated = [];

    reasons.forEach(reason => {
      months.forEach(month => {
        const entries = historicalStats.filter(item => item.cancellationReason === reason && moment(item.date).isSame(month, 'month'));
        const count = entries.reduce((sum, item) => sum + (+item.count), 0);
        const existingEntry = aggregated.find(entry => entry.reason === reason);

        if (existingEntry) {
          if (!existingEntry.data) {
            existingEntry.data = [];
          }
          existingEntry.data.push(count);
        } else {
          aggregated.push({ reason: reason, data: [count] });
        }
      })
    });

    return aggregated.map(entry => {
      return {
        reason: capitalizeOnlyFirstLetter(entry.reason),
        data: entry.data,
      }
    });
  }

  get upcomingCancellationCountsByMonth() {
    const upcomingStats = this.cancellationStats.filter(item =>
      item.cancellationReason === 'CANCELLING'
    );

    let months = upcomingStats.map(item => item.date)
    months = months.filter((item, index) => months.indexOf(item) === index)
      .map(item => moment(item))
      .sort((a, b) => a.unix() - b.unix());

    let reasons = upcomingStats.map(item => item.cancellationReason)
    reasons = reasons.filter((item, index) => reasons.indexOf(item) === index);

    const aggregated = [];

    reasons.forEach(reason => {
      months.forEach(month => {
        const entries = upcomingStats.filter(item => item.cancellationReason === reason && moment(item.date).isSame(month, 'month'));
        const count = entries.reduce((sum, item) => sum + (+item.count), 0);
        const existingEntry = aggregated.find(entry => entry.reason === reason);

        if (existingEntry) {
          if (!existingEntry.data) {
            existingEntry.data = [];
          }
          existingEntry.data.push(count);
        } else {
          aggregated.push({ reason: reason, data: [count] });
        }
      })
    });

    return aggregated.map(entry => {
      return {
        reason: capitalizeOnlyFirstLetter(entry.reason),
        data: entry.data,
      }
    });
  }

  private updateHistoricalCancellationsChartData() {
    const historicalStats = this.cancellationStats.filter(item =>
      item.cancellationReason === 'CANCELLED' || item.cancellationReason === 'LAPSED'
    );

    let months = historicalStats.map(item => item.date)
    months = months.filter((item, index) => months.indexOf(item) === index)
      .map(item => moment(item))
      .sort((a, b) => a.unix() - b.unix());

    const datasets = [];
    this.historicalCancellationCountsByMonth.map((entry, index) => {
      return [
        {
          label: entry.reason,
          data: entry.data.map((count, mIndex) => {
            return {
              x: months[mIndex],
              y: count
            }
          }),
          borderColor: Colors[index % Colors.length],
          backgroundColor: Colors[index % Colors.length],
          fill: false,
          lineTension: 0.2,
        },
        {
          label: `${entry.reason} Trend`,
          data: this.calculateTrendLine(entry.data).map((count, mIndex) => {
            return {
              x: months[mIndex],
              y: count
            }
          }),
          borderColor: ColorsTrend[index % Colors.length],
          backgroundColor: ColorsTrend[index % Colors.length],
          fill: false,
          borderWidth: 1.5,
          borderDash: [6, 2],
          lineTension: 0,
        }
      ]
    }).forEach(entry => datasets.push(...entry));

    const chartConfig = {
      type: 'line',
      data: {
        datasets,
      },
      options: {
        plugins: {
          datalabels: false
        },
        legend: {
          position: 'left',
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        colors: {
          enabled: true
        },
        title: {
          display: true,
          text: `Historical cancellations of ${this.subscriptionType} subscriptions in the last ${this.cancellationPeriod} months`,
          fontSize: 16,
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: {
                month: 'MMM YYYY'
              },
              min: months.length > 0 ? months[0] : undefined,
              max: moment()
            }
          }],
          yAxes: [{
            ticks: {
              min: 0
            }
          }]
        }
      }
    };

    if (this.historicalCancellationsChart) {
      this.historicalCancellationsChart.destroy();
    }
    const ctx = (document.getElementById('historicalCancellationsChartCanvas') as HTMLCanvasElement).getContext('2d');
    this.historicalCancellationsChart = new Chart(ctx, chartConfig)
  }

  private updateUpcomingCancellationsChartData() {
    const upcomingStats = this.cancellationStats.filter(item =>
      item.cancellationReason === 'CANCELLING'
    );

    let months = upcomingStats.map(item => item.date)
    months = months.filter((item, index) => months.indexOf(item) === index)
      .map(item => moment(item))
      .sort((a, b) => a.unix() - b.unix());

    const datasets = [];
    this.upcomingCancellationCountsByMonth.map((entry, index) => {
      return [
        {
          label: entry.reason,
          data: entry.data.map((count, mIndex) => {
            return {
              x: months[mIndex],
              y: count
            }
          }),
          borderColor: Colors[index % Colors.length],
          backgroundColor: Colors[index % Colors.length],
          fill: false,
          lineTension: 0.2,
        },
        {
          label: `${entry.reason} Trend`,
          data: this.calculateTrendLine(entry.data).map((count, mIndex) => {
            return {
              x: months[mIndex],
              y: count
            }
          }),
          borderColor: ColorsTrend[index % Colors.length],
          backgroundColor: ColorsTrend[index % Colors.length],
          fill: false,
          borderWidth: 1.5,
          borderDash: [6, 2],
          lineTension: 0,
        }
      ]
    }).forEach(entry => datasets.push(...entry));

    const chartConfig = {
      type: 'line',
      data: {
        datasets,
      },
      options: {
        plugins: {
          datalabels: false
        },
        legend: {
          position: 'right',
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        colors: {
          enabled: true
        },
        title: {
          display: true,
          text: `Upcoming cancellations of ${this.subscriptionType} subscriptions`,
          fontSize: 16,
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: {
                month: 'MMM YYYY'
              },
              min: moment(),
              max: months.length > 0 ? months[months.length - 1] : moment().add(12, 'months')
            }
          }],
          yAxes: [{
            ticks: {
              min: 0
            }
          }]
        }
      }
    };

    if (this.upcomingCancellationsChart) {
      this.upcomingCancellationsChart.destroy();
    }
    const ctx = (document.getElementById('upcomingCancellationsChartCanvas') as HTMLCanvasElement).getContext('2d');
    this.upcomingCancellationsChart = new Chart(ctx, chartConfig)
  }
}

export function capitalizeOnlyFirstLetter(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}



// WEBPACK FOOTER //
// /Users/thxgg/Projects/Work/code-hospitality-cms/src/app/exit-survey/exit-survey-list/cancellations-statistics/cancellations-statistics.component.ts